import { Button } from '@/shared/ui/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/shadcn/command';
import type { MedicationSearchRow } from '../../model/useMedicationSearchPicker';

interface MedicationSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchText: string;
  onSearchTextChange: (text: string) => void;
  onCloseResetSearchText: () => void;
  isSearchTextValid: boolean;
  searchTextError: string | null;
  isSelecting: boolean;
  isSearchPending: boolean;
  isSearchError: boolean;
  onRetrySearch: () => void;
  results: MedicationSearchRow[];
  onSelectMedication: (row: MedicationSearchRow) => void;
  selectError: string | null;
  triggerLabel: string;
  triggerDisabled: boolean;
  triggerAriaInvalid?: boolean;
}

export function MedicationSearchDialog({
  open,
  onOpenChange,
  searchText,
  onSearchTextChange,
  onCloseResetSearchText,
  isSearchTextValid,
  searchTextError,
  isSelecting,
  isSearchPending,
  isSearchError,
  onRetrySearch,
  results,
  onSelectMedication,
  selectError,
  triggerLabel,
  triggerDisabled,
  triggerAriaInvalid,
}: MedicationSearchDialogProps) {
  const closeSearch = () => {
    onOpenChange(false);
    onCloseResetSearchText();
  };

  return (
    <div
      className="relative"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          closeSearch();
        }
      }}
    >
      <Command
        shouldFilter={false}
        className="overflow-visible rounded-none bg-transparent [&_[data-slot=command-input-wrapper]]:h-9 [&_[data-slot=command-input-wrapper]]:rounded-full [&_[data-slot=command-input-wrapper]]:border [&_[data-slot=command-input-wrapper]]:border-input [&_[data-slot=command-input-wrapper]]:bg-transparent [&_[data-slot=command-input-wrapper]]:px-3 [&_[data-slot=command-input-wrapper]]:shadow-xs"
      >
        <CommandInput
          value={isSelecting ? 'Loading medication…' : open ? searchText : triggerLabel}
          onValueChange={(value) => {
            if (!open) {
              onOpenChange(true);
            }
            onSearchTextChange(value);
          }}
          onFocus={() => {
            if (!open) {
              onCloseResetSearchText();
              onOpenChange(true);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              closeSearch();
            }
          }}
          placeholder="Search medications"
          aria-invalid={triggerAriaInvalid}
          aria-busy={isSelecting}
          disabled={triggerDisabled}
          className="h-9 py-2 text-sm"
        />
        {open ? (
          <CommandList className="absolute top-full right-0 left-0 z-50 mt-2 max-h-80 overscroll-none rounded-xl border border-border bg-popover p-1 shadow-lg">
            {searchText.trim().length === 0 ? (
              <CommandEmpty>Type at least 3 characters to search.</CommandEmpty>
            ) : !isSearchTextValid ? (
              <CommandEmpty>
                {searchTextError ?? 'Type at least 3 characters to search.'}
              </CommandEmpty>
            ) : isSelecting ? (
              <CommandEmpty>Loading medication details…</CommandEmpty>
            ) : isSearchPending ? (
              <CommandEmpty>Searching…</CommandEmpty>
            ) : isSearchError ? (
              <CommandEmpty>
                Search failed.
                <div className="mt-3 flex justify-center">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => void onRetrySearch()}
                  >
                    Retry
                  </Button>
                </div>
              </CommandEmpty>
            ) : results.length === 0 ? (
              <CommandEmpty>No medications found.</CommandEmpty>
            ) : (
              <CommandGroup heading="Medications">
                {results.map((row) => (
                  <CommandItem
                    key={String(row.dispensableDrugId)}
                    value={String(row.dispensableDrugId)}
                    disabled={row.isObsolete}
                    onSelect={() => onSelectMedication(row)}
                  >
                    <div className="flex min-w-0 flex-col">
                      <div className="truncate">
                        {row.label}
                        {row.isObsolete ? ' (no longer marketed)' : ''}
                      </div>
                      {row.strength ? (
                        <div className="truncate text-xs text-muted-foreground">{row.strength}</div>
                      ) : null}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {selectError ? (
              <div className="border-t px-3 py-2 text-sm text-destructive">{selectError}</div>
            ) : null}
          </CommandList>
        ) : null}
      </Command>
    </div>
  );
}
